import React, { ComponentProps, ComponentType, ReactElement, useMemo, useState } from "react";

import { Button, Collapse, NumberInput, portexColor, TextInput } from "@portex-pro/ui-components";
import classNames from "classnames";
import ButtonEditDetailsView from "pages/shipper/pages/quote-details-page/components/FtlReviewBookingBody/components/ButtonEditDetailsView";
import { useTranslation } from "react-i18next";

import Text from "./Text";

export type EditableColumnRowProps<P = {}> = {
  label: string | ReactElement | null | ComponentType<{}>;
  labelTextProps?: Partial<ComponentProps<typeof Text>>;
  subLabel?: string | ReactElement | null | ComponentType<{}>;
  hide?: boolean;
  customEditingComponent?: ReactElement | ComponentType<P>;
  startCollapsableSection?: boolean;
} & (
  | {
      type: "string";
      value?: string | null;
      placeholder?: string;
      editingPlaceholder?: string;
      editableProps?: ComponentProps<typeof TextInput>;
    }
  | {
      type: "number";
      value?: number | null;
      placeholder?: number;
      editingPlaceholder?: number;
      editableProps?: ComponentProps<typeof NumberInput>;
    }
  | {
      type: "custom";
      value: ReactElement | ComponentType<{}> | string;
      placeholder?: string;
      editingPlaceholder?: undefined;
      editableProps?: P;
    }
);

export interface EditableColumnListProps {
  title?: string | ReactElement | ComponentType<{}>;
  isEditing?: boolean;
  rows: EditableColumnRowProps[];
  getCollapseCopy?: (isOpen: boolean) => string | ReactElement;
  CollapseProps?: ComponentProps<typeof Collapse>;
  setEditing?: (isEditing: boolean) => void;
  onSave?: () => void;
  disableEditing?: boolean;
}

const renderEditableRow = (args: EditableColumnRowProps, isEditing: boolean) => {
  const {
    type,
    value: Value,
    customEditingComponent: CustomEditingComponent,
    editableProps,
    placeholder,
    editingPlaceholder,
  } = args;

  if (!isEditing) {
    return typeof Value === "function" ? <Value /> : <Text size="medium">{Value ?? placeholder}</Text>;
  }

  if (!!CustomEditingComponent) {
    return typeof CustomEditingComponent === "function" ? (
      <CustomEditingComponent {...editableProps} />
    ) : (
      CustomEditingComponent
    );
  }

  if (type === "string") {
    return <TextInput {...editableProps} value={Value} placeholder={editingPlaceholder} />;
  }

  if (type === "number") {
    return <NumberInput {...editableProps} value={Value ?? editingPlaceholder ?? 0} />;
  }

  return typeof Value === "function" ? <Value /> : <Text size="medium">{Value ?? placeholder}</Text>;
};

const renderRow = (row: EditableColumnRowProps, isEditing: boolean, key: number | string, isEven: boolean) => {
  const { label: Label, labelTextProps, subLabel: SubLabel, hide } = row;
  if (hide) {
    return null;
  }

  return (
    <React.Fragment key={key}>
      <div
        className={classNames("w-full flex flex-row items-center py-3 px-4", {
          "even:bg-zebra": isEven,
          "odd:bg-zebra": !isEven,
        })}
      >
        <div className="w-1/3 min-w-60 text-nowrap flex flex-col">
          {typeof Label === "function" ? (
            <Label />
          ) : (
            <Text size="medium" weight="bold" {...labelTextProps}>
              {Label}
            </Text>
          )}

          {typeof SubLabel === "function" ? <SubLabel /> : <Text size="small">{SubLabel}</Text>}
        </div>
        <div className="w-full">{renderEditableRow(row, !!isEditing)}</div>
      </div>
    </React.Fragment>
  );
};

const EditableColumnList: React.FC<EditableColumnListProps> = (props) => {
  const { t } = useTranslation(["common", "shipper"]);
  const { title: Title, rows, isEditing, getCollapseCopy, CollapseProps, setEditing, onSave, disableEditing } = props;

  const [showCollapse, setShowCollapse] = useState(true);
  const [baseRows, collapsableRows] = useMemo(() => {
    const collapseStart = rows.findIndex((row) => row.startCollapsableSection);

    if (collapseStart == -1) {
      return [rows, []];
    }

    const baseRows = rows.slice(0, collapseStart);
    const collapsableRows = rows.slice(collapseStart);

    return [baseRows, collapsableRows];
  }, [rows]);

  return (
    <div className="w-full">
      <div className="border border-grey-400 rounded w-full bg-white">
        {!!Title && (
          <div className="flex flex-row py-3 px-4 w-full border-b justify-between">
            {typeof Title === "function" ? (
              <Title />
            ) : (
              <Text size="medium" weight="bold">
                {Title}
              </Text>
            )}
            {!!setEditing &&
              (!isEditing ? (
                <ButtonEditDetailsView disabled={disableEditing} onClick={() => setEditing(true)} />
              ) : (
                <div className="flex space-x-2">
                  <Button onClick={() => setEditing(false)} variant="outlined">
                    {t("common:discardChanges")}
                  </Button>
                  <Button color="primary" variant="contained" onClick={onSave}>
                    {t("common:save")}
                  </Button>
                </div>
              ))}
          </div>
        )}

        {baseRows.map((row, i) => renderRow(row, !!isEditing, i, true))}
        {!!collapsableRows.length && (
          <>
            <Collapse {...CollapseProps} in={showCollapse}>
              {collapsableRows.map((row, i) => renderRow(row, !!isEditing, `collapse_${i}`, baseRows.length % 2 > 0))}
            </Collapse>
            <div
              className="w-full flex flex-row items-center py-3 px-4 bg-white hover:bg-grey-100 justify-center"
              onClick={() => setShowCollapse((prev) => !prev)}
            >
              <Text size="medium" weight="bold" typographyProps={{ style: { color: portexColor.blue500 } }}>
                {getCollapseCopy ? getCollapseCopy(showCollapse) : showCollapse ? t("common:hide") : t("common:show")}
              </Text>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditableColumnList;
